import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import VendorPicker from './VendorPicker';

// ── Helpers ───────────────────────────────────────────────────────────────────
function genNumber(existing) {
  const year = new Date().getFullYear();
  const used = existing.map(q => { const m = (q.quote_number||'').match(/(\d+)$/); return m ? parseInt(m[1]) : 0; }).filter(Boolean);
  const next = used.length ? Math.max(...used) + 1 : 1;
  return `QR-${year}-${String(next).padStart(3,'0')}`;
}

function fmtDate(d) {
  if (!d) return new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'});
  return d;
}

const STATUSES = ['Draft','Sent','Quote Received','Verified','Rejected'];
const STATUS_CFG = {
  'Draft':          { bg:'#f5f5f5', color:'#555'    },
  'Sent':           { bg:'#e8f0fe', color:'#1a56db' },
  'Quote Received': { bg:'#fff3e0', color:'#e65100' },
  'Verified':       { bg:'#e8f5e9', color:'#2e7d32' },
  'Rejected':       { bg:'#ffebee', color:'#c62828' },
};

const h = React.createElement;

// ── Mini UI components ────────────────────────────────────────────────────────
function Badge({status}) {
  const s = STATUS_CFG[status]||{bg:'#f5f5f5',color:'#555'};
  return h('span',{style:{background:s.bg,color:s.color,borderRadius:4,padding:'2px 8px',fontSize:11,fontWeight:700,whiteSpace:'nowrap'}},status);
}
function Btn({onClick,disabled,variant='primary',small,children,style:extra}) {
  const V={primary:{background:'#1a1a1a',color:'#fff'},secondary:{background:'#fff',color:'#333',border:'1px solid #ddd'},success:{background:'#2e7d32',color:'#fff'},danger:{background:'#ffebee',color:'#c62828',border:'1px solid #ffcdd2'},blue:{background:'#1a56db',color:'#fff'},warning:{background:'#e65100',color:'#fff'},purple:{background:'#6a1b9a',color:'#fff'}};
  return h('button',{onClick,disabled,style:{padding:small?'4px 10px':'8px 16px',border:'none',borderRadius:6,fontSize:small?11:13,fontWeight:600,cursor:disabled?'not-allowed':'pointer',opacity:disabled?0.5:1,...(V[variant]||V.primary),...(extra||{})}},children);
}
function Inp({label,value,onChange,placeholder,type='text',required,style:extra}) {
  return h('div',{style:{marginBottom:10}},
    label&&h('label',{style:{fontSize:11,fontWeight:700,color:'#555',display:'block',marginBottom:3,letterSpacing:.3}},label,required&&h('span',{style:{color:'#c62828',marginLeft:2}},'*')),
    h('input',{type,value:value||'',placeholder,onChange:e=>onChange(e.target.value),style:{width:'100%',padding:'7px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:13,boxSizing:'border-box',outline:'none',fontFamily:'inherit',...(extra||{})}})
  );
}
function Txta({label,value,onChange,placeholder,rows=2,style:extra}) {
  return h('div',{style:{marginBottom:10}},
    label&&h('label',{style:{fontSize:11,fontWeight:700,color:'#555',display:'block',marginBottom:3,letterSpacing:.3}},label),
    h('textarea',{value:value||'',placeholder,rows,onChange:e=>onChange(e.target.value),style:{width:'100%',padding:'7px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:13,boxSizing:'border-box',resize:'vertical',outline:'none',fontFamily:'inherit',...(extra||{})}})
  );
}
function Sel({label,value,onChange,children,required}) {
  return h('div',{style:{marginBottom:10}},
    label&&h('label',{style:{fontSize:11,fontWeight:700,color:'#555',display:'block',marginBottom:3,letterSpacing:.3}},label,required&&h('span',{style:{color:'#c62828',marginLeft:2}},'*')),
    h('select',{value:value||'',onChange:e=>onChange(e.target.value),style:{width:'100%',padding:'7px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:13,background:'#fff',fontFamily:'inherit'}},children)
  );
}
function SHead({children,style:extra}) {
  return h('div',{style:{fontSize:10,fontWeight:700,color:'#888',letterSpacing:1,textTransform:'uppercase',marginBottom:8,marginTop:16,borderBottom:'1px solid #f0f0f0',paddingBottom:4,...(extra||{})}},children);
}

// ── Item selector ─────────────────────────────────────────────────────────────
function ItemPicker({items, vendorName, selectedIds, onChange}) {
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  const parents = items.filter(it => !it.parentId);
  const vendorItems = parents.filter(it => vendorName && (it.vendor||'').toLowerCase() === vendorName.toLowerCase());
  const otherItems  = parents.filter(it => !vendorName || (it.vendor||'').toLowerCase() !== vendorName.toLowerCase());

  function filter(list) {
    if (!search) return list;
    const s = search.toLowerCase();
    return list.filter(it => [it.item,it.productName,it.roomName,it.project,it.vendor,it.sidemark].some(f => f&&String(f).toLowerCase().includes(s)));
  }

  const displayed = showAll ? [...filter(vendorItems),...filter(otherItems)] : filter(vendorItems);
  const allSel = displayed.length > 0 && displayed.every(it => selectedIds.includes(it.id));

  function toggle(id) { onChange(selectedIds.includes(id) ? selectedIds.filter(x=>x!==id) : [...selectedIds, id]); }
  function toggleAll() { allSel ? onChange(selectedIds.filter(id=>!displayed.find(it=>it.id===id))) : onChange([...new Set([...selectedIds,...displayed.map(it=>it.id)])]); }

  return h('div',null,
    h('div',{style:{display:'flex',gap:8,marginBottom:8,alignItems:'center'}},
      h('div',{style:{flex:1,position:'relative'}},
        h('input',{value:search,onChange:e=>setSearch(e.target.value),placeholder:'Search items…',style:{width:'100%',padding:'6px 28px 6px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:12,boxSizing:'border-box'}}),
        search&&h('button',{onClick:()=>setSearch(''),style:{position:'absolute',right:6,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:'#aaa',cursor:'pointer',fontSize:14}}, '×')
      ),
      vendorName&&h('button',{onClick:()=>setShowAll(v=>!v),style:{padding:'6px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:11,background:showAll?'#1a1a1a':'#fff',color:showAll?'#fff':'#555',cursor:'pointer',whiteSpace:'nowrap'}}, showAll?'Vendor only':'Show all items')
    ),
    vendorName&&h('div',{style:{fontSize:11,color:'#888',marginBottom:6}},
      filter(vendorItems).length,' items match vendor "',vendorName,'"',
      !showAll&&filter(otherItems).length>0&&h('span',null,' · ',filter(otherItems).length,' others hidden')
    ),
    displayed.length === 0
      ? h('div',{style:{padding:'20px',textAlign:'center',color:'#bbb',fontSize:12,border:'1px dashed #ddd',borderRadius:8}},
          vendorName&&!showAll
            ? h('div',null,'No items for this vendor. ',h('button',{onClick:()=>setShowAll(true),style:{color:'#1a56db',background:'none',border:'none',cursor:'pointer',fontSize:12,fontWeight:600}},'Show all →'))
            : 'No items match'
        )
      : h('div',{style:{border:'1px solid #e0e0e0',borderRadius:8,overflow:'hidden',maxHeight:320,overflowY:'auto'}},
          h('table',{style:{width:'100%',borderCollapse:'collapse',fontSize:12}},
            h('thead',null,h('tr',{style:{background:'#f5f5f5',position:'sticky',top:0}},
              h('th',{style:{padding:'7px 10px',borderBottom:'1px solid #e0e0e0'}},h('input',{type:'checkbox',checked:allSel,onChange:toggleAll})),
              ['Product','Project','Room','Vendor','Qty','Status'].map(hd=>h('th',{key:hd,style:{padding:'7px 8px',textAlign:'left',fontWeight:600,color:'#555',borderBottom:'1px solid #e0e0e0',fontSize:11}},hd))
            )),
            h('tbody',null, displayed.map(it => {
              const checked = selectedIds.includes(it.id);
              const diffVendor = vendorName && (it.vendor||'').toLowerCase() !== vendorName.toLowerCase();
              return h('tr',{key:it.id, onClick:()=>toggle(it.id), style:{borderBottom:'1px solid #f5f5f5',cursor:'pointer',background:checked?'#f0f4ff':diffVendor?'#fffde7':'transparent'}},
                h('td',{style:{padding:'7px 10px'}},h('input',{type:'checkbox',checked,onChange:()=>toggle(it.id),onClick:e=>e.stopPropagation()})),
                h('td',{style:{padding:'7px 8px'}},
                  h('div',{style:{fontWeight:checked?600:400}},it.productName||it.item||'—'),
                  it.productName&&h('div',{style:{fontSize:10,color:'#aaa'}},it.item)
                ),
                h('td',{style:{padding:'7px 8px',color:'#888',fontSize:11}},it.project||'—'),
                h('td',{style:{padding:'7px 8px',color:'#888',fontSize:11}},it.roomName||'—'),
                h('td',{style:{padding:'7px 8px',fontSize:11,color:diffVendor?'#e65100':'#555'}},it.vendor||'—',diffVendor&&h('div',{style:{fontSize:9,color:'#e65100'}},'diff vendor')),
                h('td',{style:{padding:'7px 8px',fontSize:11}},it.qty||'1',' ',it.qtyType||''),
                h('td',{style:{padding:'7px 8px'}},h('span',{style:{background:'#f5f5f5',color:'#555',borderRadius:3,padding:'1px 5px',fontSize:10}},it.status||'—'))
              );
            }))
          )
        ),
    selectedIds.length > 0 && h('div',{style:{fontSize:11,color:'#1a56db',marginTop:6,fontWeight:600}}, selectedIds.length,' item'+(selectedIds.length>1?'s':'')+' selected')
  );
}

// ── Quote Builder (New/Edit) ──────────────────────────────────────────────────
function QuoteBuilder({quotes, vendors, items, projects, quote, onSave, onCancel}) {
  const isEdit = !!quote;

  // Find project receiver address
  function getReceiverLines(projectLabel) {
    const proj = projects.find(p => p.label === projectLabel);
    if (!proj || !proj.receiverId) return [];
    const recv = vendors.find(v => v.id === proj.receiverId);
    if (!recv) return [];
    const lines = [recv.name];
    if (recv.shipAttn) lines.push('Attn: ' + recv.shipAttn);
    if (recv.address)  lines.push(recv.address);
    if (recv.address2) lines.push(recv.address2);
    const cityLine = [recv.city, recv.state, recv.zip].filter(Boolean).join(', ');
    if (cityLine) lines.push(cityLine);
    return lines;
  }

  // Initial state
  // Parse version from existing quote number e.g. "QR-2026-001" or "QR-2026-001-v2"
  function nextVersion(num) {
    const m = num.match(/^(.*)-v(\d+)$/);
    if (m) return `${m[1]}-v${parseInt(m[2])+1}`;
    return `${num}-v2`;
  }

  const init = quote ? {
    quote_number: quote.quote_number||'',
    vendor: quote.vendor||'',
    vendor_contact: quote.vendor_contact||'',
    vendor_email: quote.vendor_email||'',
    project: quote.project||'',
    date: quote.date||fmtDate(),
    ship_to_lines: quote.ship_to_lines||[],
    ship_to_attn: quote.ship_to_attn||'',
    resale_cert: quote.resale_cert||false,
    notes: quote.notes||'',
    item_ids: quote.item_ids||[],
    item_vendor_notes: quote.item_vendor_notes||{},
    status: quote.status||'Draft',
  } : {
    quote_number: genNumber(quotes),
    vendor: '', vendor_contact: '', vendor_email: '',
    project: '', date: fmtDate(),
    ship_to_lines: [], ship_to_attn: '',
    resale_cert: false, notes: '',
    item_ids: [], item_vendor_notes: {},
    status: 'Draft',
  };

  const [form, setForm] = useState(init);
  const [step, setStep] = useState('build'); // 'build' | 'preview'
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  function set(k) { return v => setForm(prev => ({...prev, [k]:v})); }

  // When vendor changes, auto-fill contact info
  useEffect(() => {
    if (!form.vendor) return;
    const vd = vendors.find(v => v.name === form.vendor);
    if (!vd) return;
    const c0 = (vd.contacts&&vd.contacts[0]) || {name: vd.contact||'', email: vd.email||''};
    setForm(prev => ({...prev,
      vendor_contact: prev.vendor_contact || c0.name || '',
      vendor_email:   prev.vendor_email   || c0.email || '',
    }));
  }, [form.vendor]);

  // When project changes, auto-fill ship-to from receiver
  useEffect(() => {
    if (!form.project) return;
    const lines = getReceiverLines(form.project);
    if (lines.length > 0) setForm(prev => ({...prev, ship_to_lines: lines}));
  }, [form.project]);

  // Vendor resale cert status
  const selVendor = vendors.find(v => v.name === form.vendor);
  const certOnFile = selVendor?.resaleCertOnFile || false;

  // Build vendor to_lines for PDF
  function buildToLines() {
    const lines = [];
    if (form.vendor) lines.push(form.vendor);
    if (form.vendor_contact) lines.push('Attn: ' + form.vendor_contact);
    return lines;
  }

  // Build items payload for PDF
  function buildItemsPayload() {
    return form.item_ids.map(id => {
      const it = items.find(i => i.id === id);
      if (!it) return null;
      return {
        tag:          it.item || '',
        product_name: it.productName || it.item || '',
        description:  it.description || '',
        room:         it.roomName || '',
        sidemark:     it.sidemark || '',
        manufacturer: it.manufacturer || '',
        model:        it.model || '',
        finish:       it.finish || '',
        dimensions:   it.dimensions || '',
        lead_time:    it.leadTime || '',
        vendor:       it.vendor || '',
        qty:          it.qty || '1',
        qty_type:     it.qtyType || '',
        image_url:    it.imageUrl || '',
        notes:        form.item_vendor_notes[id] || '',
      };
    }).filter(Boolean);
  }

  function buildPayload() {
    const project = form.project || '';
    const projLabel = project.replace(/^\d+-/, '');
    return {
      doc_type:      'QR',
      doc_number:    form.quote_number,
      version:       1,
      date:          form.date,
      show_pricing:  false,
      to_lines:      buildToLines(),
      opening:       projLabel
        ? `I am writing to request a quote for the following item(s) for our ${projLabel} project.`
        : 'I am writing to request a quote for the following item(s).',
      ship_to_lines: form.ship_to_lines,
      resale_cert:   form.resale_cert,
      notes:         form.notes,
      items:         buildItemsPayload(),
    };
  }

  async function generatePreview() {
    setPreviewLoading(true); setError('');
    try {
      const res = await fetch('/api/generate-doc-portrait', {
        method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(buildPayload())
      });
      if (!res.ok) throw new Error('API error ' + res.status);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setStep('preview');
    } catch(e) { setError('Could not generate preview: ' + e.message); }
    setPreviewLoading(false);
  }

  async function handleSave() {
    setSaving(true); setError('');
    try {
      // 1. Generate final PDF
      const res = await fetch('/api/generate-doc-portrait', {
        method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(buildPayload())
      });
      if (!res.ok) throw new Error('PDF generation failed');
      const pdfBlob = await res.blob();
      // 2. Save quote record to Supabase
      // On edit, bump version for the new PDF (QR-2026-001 → QR-2026-001-v2)
      const pdfRef = isEdit ? nextVersion(form.quote_number) : form.quote_number;
      const pdfFile = new File([pdfBlob], `${pdfRef}.pdf`, {type:'application/pdf'});
      const qRow = {
        quote_number: form.quote_number,  // base number never changes
        current_version: pdfRef,          // tracks latest version sent
        vendor: form.vendor,
        vendor_contact: form.vendor_contact,
        vendor_email: form.vendor_email,
        project: form.project,
        date: form.date,
        ship_to_lines: form.ship_to_lines,
        ship_to_attn: form.ship_to_attn,
        resale_cert: form.resale_cert,
        notes: form.notes,
        item_ids: form.item_ids,
        item_vendor_notes: form.item_vendor_notes,
        status: 'Sent',
      };
      let savedQuote;
      if (isEdit && quote.id) {
        const {data,error:e} = await supabase.from('quotes').update(qRow).eq('id',quote.id).select().single();
        if (e) throw new Error(e.message);
        savedQuote = data;
      } else {
        const {data,error:e} = await supabase.from('quotes').insert(qRow).select().single();
        if (e) throw new Error(e.message);
        savedQuote = data;
      }

      // 3. Upload PDF and attach to each item's documents
      for (const itemId of form.item_ids) {
        const path = `item-docs/${itemId}/${pdfRef}-${Date.now()}.pdf`;
        const {error:upErr} = await supabase.storage.from('item-documents').upload(path, pdfFile, {upsert:true});
        if (upErr) { console.error('Quote PDF upload failed:', upErr.message); continue; }
        const {data:urlData} = supabase.storage.from('item-documents').getPublicUrl(path);
        await supabase.from('item_documents').insert({
          item_id: itemId,
          name: `${form.quote_number}.pdf`,
          path, url: urlData?.publicUrl||'',
          type: 'Quote Request', doc_type: 'Quote Request',
          label: pdfRef,
          file_name: `${form.quote_number}.pdf`,
          file_path: path,
          mime_type: 'application/pdf',
          size_bytes: pdfFile.size,
          direction: 'outgoing',
          created_at: new Date().toISOString(),
        });
      }

      // 4. Item statuses: NO write (2026-07-28). 'Quote Requested' left
      // the status system (folded into Specifying — tracker rebuild);
      // the quote record saved above IS the request's audit trail. The
      // old unconditional write also walked back items already past
      // Quote Received.

      onSave(savedQuote);

      // 5. Auto-download resale cert if checkbox is checked
      if (form.resale_cert && form.vendor) {
        fetch('/api/resale-cert', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({vendorName: form.vendor}),
        }).then(r => r.blob()).then(blob => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `ResaleCert_${form.vendor.replace(/\s+/g,'_')}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }).catch(e => console.error('Resale cert download failed:', e));
      }
    } catch(e) { setError('Save failed: ' + e.message); }
    setSaving(false);
  }

  // ── Preview step ────────────────────────────────────────────────────────────
  if (step === 'preview') {
    return h('div',null,
      h('div',{style:{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}},
        h('div',null,
          h('div',{style:{fontSize:18,fontWeight:700}},form.quote_number),
          h('div',{style:{fontSize:12,color:'#888',marginTop:2}},'Preview — review before sending')
        ),
        h('div',{style:{display:'flex',gap:8}},
          h(Btn,{variant:'secondary',onClick:()=>setStep('build')},'← Edit'),
          h(Btn,{variant:'success',onClick:handleSave,disabled:saving},saving?'Saving…':'✓ Confirm & Save')
        )
      ),
      error&&h('div',{style:{background:'#ffebee',border:'1px solid #ffcdd2',borderRadius:6,padding:'8px 12px',fontSize:12,color:'#c62828',marginBottom:12}},error),
      h('div',{style:{background:'#e8f5e9',border:'1px solid #a5d6a7',borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#1b5e20'}},
        '✓ PDF looks good? Click Confirm & Save to attach to ',form.item_ids.length,' item'+(form.item_ids.length!==1?'s':'')+' and update status to Quote Requested.'
      ),
      h('iframe',{src:previewUrl,style:{width:'100%',height:'70vh',border:'1px solid #e0e0e0',borderRadius:8,background:'#fff'}})
    );
  }

  // ── Build step ──────────────────────────────────────────────────────────────
  const selectedItems = form.item_ids.map(id=>items.find(i=>i.id===id)).filter(Boolean);

  return h('div',null,
    // Header
    h('div',{style:{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}},
      h('div',null,
        h('div',{style:{fontSize:18,fontWeight:700}},isEdit?'Edit Quote Request':'New Quote Request'),
        h('div',{style:{fontSize:12,color:'#888',marginTop:2}},form.quote_number)
      ),
      h('div',{style:{display:'flex',gap:8}},
        h(Btn,{variant:'secondary',onClick:onCancel},'Cancel'),
        h(Btn,{variant:'blue',onClick:generatePreview,disabled:previewLoading||form.item_ids.length===0||!form.vendor},
          previewLoading?'Generating…':'Preview PDF →')
      )
    ),

    error&&h('div',{style:{background:'#ffebee',border:'1px solid #ffcdd2',borderRadius:6,padding:'8px 12px',fontSize:12,color:'#c62828',marginBottom:12}},error),

    h('div',{style:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:24}},

      // Left: quote details
      h('div',null,
        h(SHead,null,'Quote Details'),
        h('div',{style:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}},
          h(Inp,{label:'Quote Number',value:form.quote_number,onChange:set('quote_number')}),
          h(Inp,{label:'Date',value:form.date,onChange:set('date'),placeholder:'March 28, 2026'})
        ),

        h(SHead,null,'Vendor'),
        // allowAdd=false: Vendor creation is concentrated in
        // VendorDirectory and item-creation surfaces. PO/Quote contexts
        // must reference existing vendors so that vendor data quality
        // decisions happen at one of two well-known surfaces.
        h(VendorPicker, {
          value: form.vendor,
          onChange: set('vendor'),
          vendors: vendors.filter(v => (v.vendorType || 'Vendor') !== 'Receiver'),
          required: true,
          allowAdd: false,
        }),
        h('div',{style:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}},
          h(Inp,{label:'Contact',value:form.vendor_contact,onChange:set('vendor_contact'),placeholder:'Name at vendor'}),
          h(Inp,{label:'Email',value:form.vendor_email,onChange:set('vendor_email'),placeholder:'vendor@example.com',type:'email'})
        ),

        // Resale cert
        h('div',{style:{marginBottom:14,background:'#fafafa',border:'1px solid #eee',borderRadius:6,padding:'10px 12px'}},
          h('div',{style:{fontSize:10,fontWeight:700,color:'#888',letterSpacing:.5,textTransform:'uppercase',marginBottom:8}},'Resale Certificate'),
          h('label',{style:{display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer'}},
            h('input',{type:'checkbox',checked:form.resale_cert,onChange:e=>set('resale_cert')(e.target.checked)}),
            'Include CA Resale Certificate (CDTFA-230)'
          ),
          !certOnFile&&form.vendor&&h('div',{style:{fontSize:11,color:'#e65100',marginTop:6}},
            '⚠ No cert on file for this vendor — generate one in Vendor Directory first.'
          ),
          certOnFile&&h('div',{style:{fontSize:11,color:'#2e7d32',marginTop:6}},'✓ Cert on file for this vendor')
        ),
        h(SHead,null,'Project & Ship To'),
        h(Sel,{label:'Project',value:form.project,onChange:set('project')},
          h('option',{value:''},'— Select project —'),
          projects.map(p=>h('option',{key:p.id,value:p.label},p.label))
        ),
        h(Txta,{label:'Ship To Address',value:(form.ship_to_lines||[]).join('\n'),onChange:v=>set('ship_to_lines')(v.split('\n')),placeholder:'Auto-filled from project receiver\nor enter manually',rows:3}),

        h(Txta,{label:'Quote Notes',value:form.notes,onChange:set('notes'),placeholder:'Any additional notes for this quote request…',rows:2})
      ),

      // Right: item selection
      h('div',null,
        h(SHead,null,'Select Items'),
        h(ItemPicker,{items, vendorName:form.vendor, selectedIds:form.item_ids, onChange:set('item_ids')}),

        // Per-item vendor notes
        selectedItems.length > 0 && h('div',{style:{marginTop:16}},
          h(SHead,null,'Per-Item Notes for Vendor'),
          h('div',{style:{fontSize:11,color:'#888',marginBottom:8}},'Optional notes per item — these appear on the quote. Internal item notes are excluded.'),
          selectedItems.map(it => h('div',{key:it.id,style:{marginBottom:10,background:'#fafafa',border:'1px solid #eee',borderRadius:6,padding:'10px 12px'}},
            h('div',{style:{fontSize:12,fontWeight:600,color:'#333',marginBottom:4}},it.productName||it.item),
            h('div',{style:{fontSize:10,color:'#888',marginBottom:6}},
              [it.sidemark,it.roomName,it.project].filter(Boolean).join(' · ')
            ),
            h('input',{
              value: form.item_vendor_notes[it.id]||'',
              onChange: e => setForm(prev=>({...prev,item_vendor_notes:{...prev.item_vendor_notes,[it.id]:e.target.value}})),
              placeholder: 'Notes for vendor about this item…',
              style:{width:'100%',padding:'6px 10px',border:'1px solid #ddd',borderRadius:5,fontSize:12,boxSizing:'border-box',fontFamily:'inherit'}
            })
          ))
        )
      )
    )
  );
}

// ── Quote Detail View ─────────────────────────────────────────────────────────
function QuoteDetail({quote, items, vendors, onEdit, onClose}) {
  const linkedItems = (quote.item_ids||[]).map(id=>items.find(i=>i.id===id)).filter(Boolean);
  return h('div',null,
    h('div',{style:{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}},
      h('div',null,
        h('div',{style:{fontSize:20,fontWeight:700}},quote.quote_number),
        h('div',{style:{display:'flex',gap:12,alignItems:'center',marginTop:4}},
          h('span',{style:{fontSize:13,color:'#555'}},quote.vendor),
          h(Badge,{status:quote.status}),
          h('span',{style:{fontSize:11,color:'#888'}},linkedItems.length,' item'+(linkedItems.length!==1?'s':'')),
          quote.project&&h('span',{style:{fontSize:11,color:'#888'}},quote.project),
          quote.current_version&&h('span',{style:{fontSize:11,color:'#1a56db',fontWeight:600}},quote.current_version)
        )
      ),
      h('div',{style:{display:'flex',gap:8}},
        h(Btn,{variant:'secondary',onClick:onClose},'← Back'),
        h(Btn,{variant:'secondary',onClick:onEdit},'✎ Edit')
      )
    ),

    // Meta cards
    h('div',{style:{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:20}},
      h('div',{style:{background:'#fafafa',border:'1px solid #e0e0e0',borderRadius:8,padding:14}},
        h('div',{style:{fontSize:10,fontWeight:700,color:'#888',letterSpacing:.5,marginBottom:6}},'VENDOR'),
        h('div',{style:{fontWeight:600}},quote.vendor),
        quote.vendor_contact&&h('div',{style:{fontSize:11,color:'#555',marginTop:2}},quote.vendor_contact),
        quote.vendor_email&&h('div',{style:{fontSize:11,color:'#888'}},quote.vendor_email)
      ),
      h('div',{style:{background:'#fafafa',border:'1px solid #e0e0e0',borderRadius:8,padding:14}},
        h('div',{style:{fontSize:10,fontWeight:700,color:'#888',letterSpacing:.5,marginBottom:6}},'SHIP TO'),
        (quote.ship_to_lines||[]).map((l,i)=>h('div',{key:i,style:{fontSize:11,color:'#555'}},l))
      ),
      h('div',{style:{background:'#fafafa',border:'1px solid #e0e0e0',borderRadius:8,padding:14}},
        h('div',{style:{fontSize:10,fontWeight:700,color:'#888',letterSpacing:.5,marginBottom:6}},'RESALE CERT'),
        h('div',{style:{fontWeight:600,color:quote.resale_cert?'#2e7d32':'#e65100'}},
          quote.resale_cert?'✓ Attached':'⚠ Not attached'),
        quote.date&&h('div',{style:{fontSize:11,color:'#888',marginTop:6}},'Sent: ',quote.date)
      )
    ),

    // Items table
    linkedItems.length>0&&h('div',null,
      h(SHead,null,'ITEMS ON THIS QUOTE'),
      h('div',{style:{border:'1px solid #e0e0e0',borderRadius:8,overflow:'hidden'}},
        h('table',{style:{width:'100%',borderCollapse:'collapse',fontSize:11}},
          h('thead',null,h('tr',{style:{background:'#f5f5f5'}},
            ['Product','Room','Vendor','Qty','Sidemark','Vendor Notes'].map(hd=>
              h('th',{key:hd,style:{padding:'7px 10px',textAlign:'left',fontWeight:600,color:'#555',borderBottom:'1px solid #e0e0e0',fontSize:10}},hd)
            )
          )),
          h('tbody',null, linkedItems.map(it=>h('tr',{key:it.id,style:{borderBottom:'1px solid #f0f0f0'}},
            h('td',{style:{padding:'7px 10px',fontWeight:600}},it.productName||it.item||'—'),
            h('td',{style:{padding:'7px 10px',color:'#888'}},it.roomName||'—'),
            h('td',{style:{padding:'7px 10px',color:'#888'}},it.vendor||'—'),
            h('td',{style:{padding:'7px 10px'}},it.qty||'1',' ',it.qtyType||''),
            h('td',{style:{padding:'7px 10px',color:'#6a1b9a',fontSize:10}},it.sidemark||'—'),
            h('td',{style:{padding:'7px 10px',color:'#555',fontSize:11}},(quote.item_vendor_notes||{})[it.id]||'—')
          )))
        )
      )
    ),

    quote.notes&&h('div',{style:{marginTop:16}},
      h(SHead,null,'NOTES'),
      h('div',{style:{fontSize:12,color:'#555'}},quote.notes)
    )
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function QuotesTab({items, vendors, projects, onUpdateItem}) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // list | new | edit | detail
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [filterProject, setFilterProject] = useState('');

  useEffect(()=>{ loadQuotes(); },[]);

  async function loadQuotes() {
    setLoading(true);
    const {data,error} = await supabase.from('quotes').select('*').order('created_at',{ascending:false});
    if (!error) setQuotes(data||[]);
    setLoading(false);
  }

  function handleSave(saved) {
    setQuotes(prev => { const ex=prev.find(q=>q.id===saved.id); return ex?prev.map(q=>q.id===saved.id?saved:q):[saved,...prev]; });
    setSelected(saved); setMode('detail');
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this quote request?')) return;
    await supabase.from('quotes').delete().eq('id',id);
    setQuotes(prev=>prev.filter(q=>q.id!==id));
    setMode('list'); setSelected(null);
  }

  async function handleStatusChange(quote, newStatus) {
    const {data} = await supabase.from('quotes').update({status:newStatus}).eq('id',quote.id).select().single();
    if (data) { setQuotes(prev=>prev.map(q=>q.id===quote.id?data:q)); setSelected(data); }
  }

  const filtered = quotes.filter(q => {
    const ms = !search||(q.quote_number||'').toLowerCase().includes(search.toLowerCase())||(q.vendor||'').toLowerCase().includes(search.toLowerCase());
    return ms&&(!filterStatus||q.status===filterStatus)&&(!filterVendor||q.vendor===filterVendor)&&(!filterProject||q.project===filterProject);
  });
  const vendorNames = [...new Set(quotes.map(q=>q.vendor).filter(Boolean))].sort();
  const projectNames = [...new Set(quotes.map(q=>q.project).filter(Boolean))].sort();

  if (mode==='new')    return h(QuoteBuilder,{quotes,vendors,items,projects,onSave:handleSave,onCancel:()=>setMode('list')});
  if (mode==='edit')   return h(QuoteBuilder,{quotes,vendors,items,projects,quote:selected,onSave:handleSave,onCancel:()=>{setMode('detail');}});
  if (mode==='detail') return h('div',null,
    h(QuoteDetail,{quote:selected,items,vendors,onEdit:()=>setMode('edit'),onClose:()=>{setMode('list');setSelected(null);}}),
    h('div',{style:{display:'flex',gap:8,marginTop:16,paddingTop:16,borderTop:'1px solid #f0f0f0'}},
      h('div',{style:{fontSize:11,color:'#888',alignSelf:'center'}},'Update status:'),
      STATUSES.map(s=>s!==selected.status&&h(Btn,{key:s,variant:'secondary',small:true,onClick:()=>handleStatusChange(selected,s)},s)),
      h(Btn,{variant:'danger',small:true,onClick:()=>handleDelete(selected.id)},'Delete')
    )
  );

  // ── List view ───────────────────────────────────────────────────────────────
  return h('div',null,
    h('div',{style:{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}},
      h('div',null,
        h('div',{style:{fontSize:20,fontWeight:700}},'Quote Requests'),
        h('div',{style:{fontSize:12,color:'#888',marginTop:2}},
          quotes.length,' total · ',
          quotes.filter(q=>q.status==='Sent').length,' awaiting response · ',
          quotes.filter(q=>q.status==='Verified').length,' verified'
        )
      ),
      h(Btn,{onClick:()=>setMode('new')},'+ New Quote Request')
    ),
    h('div',{style:{display:'flex',gap:8,marginBottom:16}},
      h('input',{value:search,onChange:e=>setSearch(e.target.value),placeholder:'Search quote # or vendor…',style:{flex:1,padding:'7px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:12}}),
      h('select',{value:filterStatus,onChange:e=>setFilterStatus(e.target.value),style:{padding:'7px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:12,background:'#fff'}},
        h('option',{value:''},'All statuses'),
        STATUSES.map(s=>h('option',{key:s,value:s},s))
      ),
      h('select',{value:filterVendor,onChange:e=>setFilterVendor(e.target.value),style:{padding:'7px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:12,background:'#fff'}},
        h('option',{value:''},'All vendors'),
        vendorNames.map(v=>h('option',{key:v,value:v},v))
      ),
      h('select',{value:filterProject,onChange:e=>setFilterProject(e.target.value),style:{padding:'7px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:12,background:'#fff'}},
        h('option',{value:''},'All projects'),
        projectNames.map(p=>h('option',{key:p,value:p},p))
      )
    ),
    loading
      ? h('div',{style:{textAlign:'center',padding:'60px',color:'#888'}},'Loading…')
      : filtered.length===0
        ? h('div',{style:{textAlign:'center',padding:'60px',color:'#bbb',border:'1px dashed #ddd',borderRadius:8}},
            quotes.length===0
              ? h('div',null,
                  h('div',{style:{fontSize:32,marginBottom:12}},'📋'),
                  h('div',{style:{fontWeight:600,marginBottom:6}},'No quote requests yet'),
                  h('div',{style:{fontSize:12,marginBottom:16}},'Select items and generate your first quote request'),
                  h(Btn,{onClick:()=>setMode('new')},'+ New Quote Request')
                )
              : 'No quotes match your filters'
          )
        : h('div',{style:{border:'1px solid #e0e0e0',borderRadius:8,overflow:'hidden'}},
            h('table',{style:{width:'100%',borderCollapse:'collapse',fontSize:12}},
              h('thead',null,h('tr',{style:{background:'#f5f5f5'}},
                ['Quote #','Vendor','Project','Version','Status','Items','Cert','Date',''].map(hd=>
                  h('th',{key:hd,style:{padding:'8px 10px',textAlign:'left',fontWeight:600,color:'#555',borderBottom:'1px solid #e0e0e0',fontSize:11}},hd)
                )
              )),
              h('tbody',null, filtered.map(q=>
                h('tr',{key:q.id,onClick:()=>{setSelected(q);setMode('detail');},style:{borderBottom:'1px solid #f0f0f0',cursor:'pointer'},
                  onMouseEnter:e=>e.currentTarget.style.background='#fafafa',
                  onMouseLeave:e=>e.currentTarget.style.background='transparent'},
                  h('td',{style:{padding:'9px 10px',fontWeight:700}},q.quote_number),
                  h('td',{style:{padding:'9px 10px'}},q.vendor),
                  h('td',{style:{padding:'9px 10px',color:'#888',fontSize:11}},q.project||'—'),
              h('td',{style:{padding:'9px 10px',color:'#888',fontSize:11}},q.current_version||'v1'),
                  h('td',{style:{padding:'9px 10px'}},h(Badge,{status:q.status})),
                  h('td',{style:{padding:'9px 10px',color:'#555'}},(q.item_ids||[]).length),
                  h('td',{style:{padding:'9px 10px'}},q.resale_cert?h('span',{style:{color:'#2e7d32',fontSize:11,fontWeight:600}},'✓'):h('span',{style:{color:'#bbb'}},'—')),
                  h('td',{style:{padding:'9px 10px',color:'#888',fontSize:11}},q.date||'—'),
                  h('td',{style:{padding:'9px 10px'}},h('button',{onClick:e=>{e.stopPropagation();handleDelete(q.id);},style:{background:'none',border:'none',color:'#ddd',cursor:'pointer',fontSize:13}},'×'))
                )
              ))
            )
          )
  );
}
